<template>
  <aside
    class="app-sidebar"
    :class="{ 'app-sidebar--open': isOpen }"
    role="navigation"
    :aria-label="$t('nav.main_menu')"
  >
    <!-- Brand -->
    <div class="app-sidebar__brand">
      <img src="/logo.webp" alt="Procurement" class="app-sidebar__logo" />
      <span class="app-sidebar__title">{{ $t('app.name') }}</span>
    </div>

    <!-- Navigation -->
    <nav class="app-sidebar__nav">
      <template v-for="(group, groupIndex) in visibleGroups" :key="group.id">
        <!-- Divider between groups -->
        <div v-if="groupIndex > 0" class="app-sidebar__divider" />

        <ul class="app-sidebar__group" :aria-label="group.id">
          <li v-for="item in group.items" :key="item.routeName" class="app-sidebar__item">
            <router-link
              :to="{ name: item.routeName }"
              class="app-sidebar__link"
              :class="{ 'app-sidebar__link--active': isActive(item.routeName) }"
              @click="$emit('navigate')"
            >
              <component :is="item.icon" class="app-sidebar__icon" />
              <span class="app-sidebar__label">{{ $t(item.label) }}</span>
            </router-link>
          </li>
        </ul>
      </template>
    </nav>

    <!-- User profile at bottom -->
    <div v-if="currentUser" class="app-sidebar__user">
      <router-link
        :to="{ name: 'account' }"
        class="app-sidebar__user-link"
        @click="$emit('navigate')"
      >
        <div class="app-sidebar__avatar">
          <img
            v-if="currentUser.picture_url"
            :src="currentUser.picture_url"
            :alt="userDisplayName"
            class="app-sidebar__avatar-img"
          />
          <span v-else class="app-sidebar__avatar-initials">
            {{ userInitials }}
          </span>
        </div>
        <span class="app-sidebar__user-name">{{ userDisplayName }}</span>
      </router-link>
      <button
        class="app-sidebar__logout-btn"
        :aria-label="$t('auth.logout')"
        :title="$t('auth.logout')"
        @click="$emit('logout')"
      >
        <ArrowRightStartOnRectangleIcon class="app-sidebar__logout-icon" />
      </button>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { ArrowRightStartOnRectangleIcon } from '@heroicons/vue/24/outline'
import { navigationGroups } from '@/config/navigation'
import type { AuthUser, AccessLevel } from '@/types/auth.types'
import type { NavGroup } from '@/types/navigation.types'

const props = defineProps<{
  isOpen: boolean
  currentUser: AuthUser | null
}>()

defineEmits<{
  navigate: []
  logout: []
}>()

const route = useRoute()

/** Filter groups and items based on current user access level */
const visibleGroups = computed<NavGroup[]>(() => {
  const accessLevel: AccessLevel = props.currentUser?.access_level ?? 'access_level.user'
  return navigationGroups
    .filter((group) => group.access_levels.includes(accessLevel))
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.access_levels.includes(accessLevel)),
    }))
    .filter((group) => group.items.length > 0)
})

const isActive = (routeName: string): boolean => {
  return route.name === routeName
}

const userDisplayName = computed(() => {
  if (!props.currentUser) return ''
  return `${props.currentUser.first_name} ${props.currentUser.last_name}`
})

const userInitials = computed(() => {
  if (!props.currentUser) return ''
  return (
    (props.currentUser.first_name?.[0] ?? '') + (props.currentUser.last_name?.[0] ?? '')
  ).toUpperCase()
})
</script>

<style scoped lang="scss">
.app-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: var(--app-sidebar-width);
  background-color: var(--app-sidebar-bg);
  color: var(--app-sidebar-text);
  display: flex;
  flex-direction: column;
  z-index: 9000;
  overflow-y: auto;
  overflow-x: hidden;
  transition: transform 0.2s ease;

  // Mobile: hidden by default, slides in when open
  @media (max-width: 1055px) {
    transform: translateX(-100%);

    &--open {
      transform: translateX(0);
    }
  }
}

.app-sidebar__brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  flex-shrink: 0;
}

.app-sidebar__logo {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: contain;
}

.app-sidebar__title {
  font-size: var(--text-size-xl);
  font-weight: var(--font-weight-semibold);
  letter-spacing: -0.01em;
}

.app-sidebar__nav {
  flex: 1;
  padding: 0 12px;
  overflow-y: auto;
}

.app-sidebar__divider {
  height: 1px;
  background-color: var(--app-sidebar-divider);
  margin: 8px 12px;
}

.app-sidebar__group {
  list-style: none;
  margin: 0;
  padding: 4px 0;
}

.app-sidebar__item {
  margin: 2px 0;
}

.app-sidebar__link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 8px;
  color: var(--app-sidebar-text);
  text-decoration: none;
  font-size: var(--text-size-base);
  font-weight: var(--font-weight-medium);
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--app-sidebar-hover);
  }

  &--active {
    background-color: var(--app-sidebar-active);
    font-weight: var(--font-weight-semibold);
  }
}

.app-sidebar__icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.app-sidebar__label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.app-sidebar__user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  margin-top: auto;
  flex-shrink: 0;
  border-top: 1px solid var(--app-sidebar-divider);
}

.app-sidebar__user-link {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--app-sidebar-text);
  text-decoration: none;
  flex: 1;
  min-width: 0;
  border-radius: 6px;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.85;
  }
}

.app-sidebar__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background-color: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.app-sidebar__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.app-sidebar__avatar-initials {
  font-size: var(--text-size-sm);
  font-weight: var(--font-weight-semibold);
}

.app-sidebar__user-name {
  font-size: var(--text-size-base);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.app-sidebar__logout-btn {
  background: none;
  border: none;
  color: var(--app-sidebar-text);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.15s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
  }
}

.app-sidebar__logout-icon {
  width: 20px;
  height: 20px;
}
</style>
